import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Table, Button, Form, Alert, Spinner, ButtonGroup } from 'react-bootstrap';
import { FaChartBar, FaExclamationTriangle, FaCheckCircle, FaFileExcel, FaFilter } from 'react-icons/fa';
import { getFirestore, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import firebaseApp from '../../../firebase/credenciales';

/**
 * Dashboard de Análisis de Auditorías Cali
 * Muestra métricas, tendencias y análisis histórico de todas las auditorías
 */
const CaliDashboard = ({ usuario }) => {
    const [loading, setLoading] = useState(true);
    const [auditorias, setAuditorias] = useState([]);
    const [metricas, setMetricas] = useState(null);
    const [filtroTipo, setFiltroTipo] = useState('TODOS'); // TODOS, CRITICO, ADVERTENCIA
    const [filtroPeriodo, setFiltroPeriodo] = useState('30'); // 7, 30, 90, TODO

    // Cargar auditorías desde Firestore
    useEffect(() => {
        if (!usuario?.ruc) return;
        cargarAuditorias();
    }, [usuario, filtroPeriodo]);

    const cargarAuditorias = async () => {
        setLoading(true);
        try {
            const db = getFirestore(firebaseApp);
            const auditoriasRef = collection(db, 'cali_reviews');

            // Calcular fecha límite según filtro
            let fechaLimite = null;
            if (filtroPeriodo !== 'TODO') {
                fechaLimite = new Date();
                fechaLimite.setDate(fechaLimite.getDate() - parseInt(filtroPeriodo));
            }

            let q = query(
                auditoriasRef,
                where('ruc_empresa', '==', usuario.ruc),
                orderBy('fecha_revision', 'desc'),
                limit(100)
            );

            const snapshot = await getDocs(q);
            const data = [];

            snapshot.forEach(doc => {
                const auditoria = doc.data();
                // Filtrar por fecha si aplica
                if (!fechaLimite || auditoria.fecha_revision?.toDate() >= fechaLimite) {
                    data.push({ id: doc.id, ...auditoria });
                }
            });

            setAuditorias(data);
            calcularMetricas(data);
        } catch (error) {
            console.error('Error cargando auditorías:', error);
        } finally {
            setLoading(false);
        }
    };

    const calcularMetricas = (data) => {
        const totalAuditorias = data.length;
        let totalObservaciones = 0;
        let totalCriticos = 0;
        let totalAdvertencias = 0;
        let aprobadas = 0;
        const erroresPorCampo = {};
        const erroresPorPeriodo = {};

        data.forEach(auditoria => {
            const obs = auditoria.observaciones || [];
            totalObservaciones += obs.length;

            if (auditoria.aprobado) aprobadas++;

            obs.forEach(o => {
                if (o.tipo === 'CRITICO') totalCriticos++;
                else if (o.tipo === 'ADVERTENCIA') totalAdvertencias++;

                // Contar por campo
                const campo = o.campo || 'Sin clasificar';
                erroresPorCampo[campo] = (erroresPorCampo[campo] || 0) + 1;
            });

            // Contar por periodo
            const periodo = auditoria.planilla_id?.split('_')[1] || 'Sin periodo';
            erroresPorPeriodo[periodo] = (erroresPorPeriodo[periodo] || 0) + obs.length;
        });

        // Ordenar top errores
        const topErrores = Object.entries(erroresPorCampo)
            .map(([campo, cantidad]) => ({ campo, cantidad }))
            .sort((a, b) => b.cantidad - a.cantidad)
            .slice(0, 10);

        setMetricas({
            total: totalAuditorias,
            aprobadas,
            rechazadas: totalAuditorias - aprobadas,
            porcentajeAprobacion: totalAuditorias > 0 ? ((aprobadas / totalAuditorias) * 100).toFixed(1) : 0,
            totalObservaciones,
            totalCriticos,
            totalAdvertencias,
            promedioErroresPorPlanilla: totalAuditorias > 0 ? (totalObservaciones / totalAuditorias).toFixed(1) : 0,
            topErrores,
            erroresPorPeriodo
        });
    };

    const observacionesFiltradas = () => {
        let todasObservaciones = [];
        auditorias.forEach(aud => {
            (aud.observaciones || []).forEach(obs => {
                todasObservaciones.push({
                    ...obs,
                    periodo: aud.planilla_id.split('_')[1],
                    fecha: aud.fecha_revision
                });
            });
        });

        if (filtroTipo !== 'TODOS') {
            todasObservaciones = todasObservaciones.filter(obs => obs.tipo === filtroTipo);
        }

        return todasObservaciones.sort((a, b) => {
            // CRÍTICOS primero
            if (a.tipo === 'CRITICO' && b.tipo !== 'CRITICO') return -1;
            if (a.tipo !== 'CRITICO' && b.tipo === 'CRITICO') return 1;
            return 0;
        });
    };

    const exportarExcel = () => {
        const obs = observacionesFiltradas();
        if (obs.length === 0) {
            alert('No hay datos para exportar');
            return;
        }

        let csv = 'Periodo,Empleado,DNI,Tipo,Campo,Mensaje,Fecha\n';
        obs.forEach(o => {
            const mensaje = (o.mensaje || '').replace(/"/g, '""');
            const fecha = o.fecha?.toDate()?.toLocaleDateString('es-PE') || '';
            csv += `"${o.periodo}","${o.nombres || ''}","${o.dni || ''}","${o.tipo}","${o.campo || ''}","${mensaje}","${fecha}"\n`;
        });

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `cali_analisis_${filtroPeriodo}dias_${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Cargando análisis de auditorías...</p>
            </div>
        );
    }

    if (!metricas) {
        return (
            <Alert variant="warning">
                No hay auditorías disponibles para analizar.
            </Alert>
        );
    }

    return (
        <div>
            {/* Filtros */}
            <Card className="mb-4">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col md={6}>
                            <Form.Label><FaFilter className="me-2" />Periodo</Form.Label>
                            <Form.Select value={filtroPeriodo} onChange={(e) => setFiltroPeriodo(e.target.value)}>
                                <option value="7">Últimos 7 días</option>
                                <option value="30">Últimos 30 días</option>
                                <option value="90">Últimos 90 días</option>
                                <option value="TODO">Todos los registros</option>
                            </Form.Select>
                        </Col>
                        <Col md={6} className="mt-3 mt-md-0">
                            <Form.Label>Tipo de Observación</Form.Label>
                            <ButtonGroup className="w-100">
                                <Button
                                    variant={filtroTipo === 'TODOS' ? 'primary' : 'outline-primary'}
                                    onClick={() => setFiltroTipo('TODOS')}
                                    size="sm"
                                >
                                    Todos
                                </Button>
                                <Button
                                    variant={filtroTipo === 'CRITICO' ? 'danger' : 'outline-danger'}
                                    onClick={() => setFiltroTipo('CRITICO')}
                                    size="sm"
                                >
                                    Críticos
                                </Button>
                                <Button
                                    variant={filtroTipo === 'ADVERTENCIA' ? 'warning' : 'outline-warning'}
                                    onClick={() => setFiltroTipo('ADVERTENCIA')}
                                    size="sm"
                                >
                                    Advertencias
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Resumen Ejecutivo */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="text-center h-100">
                        <Card.Body>
                            <h6 className="text-muted">Total Auditorías</h6>
                            <h2 className="text-primary">{metricas.total}</h2>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center h-100">
                        <Card.Body>
                            <h6 className="text-muted">% Aprobadas</h6>
                            <h2 className={metricas.porcentajeAprobacion >= 80 ? 'text-success' : 'text-warning'}>
                                {metricas.porcentajeAprobacion}%
                            </h2>
                            <small className="text-muted">{metricas.aprobadas} de {metricas.total}</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center h-100">
                        <Card.Body>
                            <h6 className="text-muted">Errores Críticos</h6>
                            <h2 className="text-danger">{metricas.totalCriticos}</h2>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center h-100">
                        <Card.Body>
                            <h6 className="text-muted">Advertencias</h6>
                            <h2 className="text-warning">{metricas.totalAdvertencias}</h2>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Top 10 Errores Más Frecuentes */}
            <Row className="mb-4">
                <Col md={6}>
                    <Card>
                        <Card.Header>
                            <FaChartBar className="me-2" />
                            Top 10 Tipos de Error Más Frecuentes
                        </Card.Header>
                        <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {metricas.topErrores.length > 0 ? (
                                <Table size="sm" striped hover>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Campo</th>
                                            <th className="text-end">Cantidad</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {metricas.topErrores.map((item, idx) => (
                                            <tr key={idx}>
                                                <td>{idx + 1}</td>
                                                <td>
                                                    <code>{item.campo}</code>
                                                </td>
                                                <td className="text-end">
                                                    <Badge bg="danger">{item.cantidad}</Badge>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <Alert variant="success">
                                    <FaCheckCircle className="me-2" />
                                    ¡Sin errores registrados!
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Errores por Periodo */}
                <Col md={6}>
                    <Card>
                        <Card.Header>
                            <FaExclamationTriangle className="me-2" />
                            Observaciones por Periodo
                        </Card.Header>
                        <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <Table size="sm" striped hover>
                                <thead>
                                    <tr>
                                        <th>Periodo</th>
                                        <th className="text-end">Total Observaciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(metricas.erroresPorPeriodo)
                                        .sort((a, b) => b[1] - a[1])
                                        .slice(0, 10)
                                        .map(([periodo, cantidad], idx) => (
                                            <tr key={idx}>
                                                <td><strong>{periodo}</strong></td>
                                                <td className="text-end">
                                                    <Badge bg={cantidad > 10 ? 'danger' : cantidad > 5 ? 'warning' : 'info'}>
                                                        {cantidad}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Detalle de Observaciones */}
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <span>Detalle de Observaciones ({observacionesFiltradas().length})</span>
                    <Button size="sm" variant="success" onClick={exportarExcel}>
                        <FaFileExcel className="me-1" /> Exportar a Excel
                    </Button>
                </Card.Header>
                <Card.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <Table size="sm" striped hover responsive>
                        <thead className="sticky-top bg-white">
                            <tr>
                                <th>Periodo</th>
                                <th>Empleado</th>
                                <th>Tipo</th>
                                <th>Campo</th>
                                <th>Mensaje</th>
                            </tr>
                        </thead>
                        <tbody>
                            {observacionesFiltradas().slice(0, 100).map((obs, idx) => (
                                <tr key={idx}>
                                    <td><Badge bg="secondary">{obs.periodo}</Badge></td>
                                    <td>
                                        <div className="small">
                                            <strong>{obs.nombres}</strong>
                                            <br />
                                            <span className="text-muted">{obs.dni}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <Badge bg={obs.tipo === 'CRITICO' ? 'danger' : 'warning'}>
                                            {obs.tipo}
                                        </Badge>
                                    </td>
                                    <td><code className="small">{obs.campo}</code></td>
                                    <td className="small">{obs.mensaje}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    {observacionesFiltradas().length > 100 && (
                        <Alert variant="info" className="mt-3">
                            Mostrando las primeras 100 observaciones. Exporta a Excel para ver el listado completo.
                        </Alert>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default CaliDashboard;
